package com.partejar.app.feature.home.data.remote

import com.partejar.app.feature.home.data.HomeAppointmentData
import com.partejar.app.feature.home.data.HomeAttentionData
import com.partejar.app.feature.home.data.HomeAttentionLevel
import com.partejar.app.feature.home.data.HomeDashboardData
import com.partejar.app.feature.home.data.HomeDateTimeProvider
import com.partejar.app.feature.home.data.HomePayloadException
import com.partejar.app.feature.home.data.HomePendingType
import com.partejar.app.feature.home.data.HomePregnancyData
import com.partejar.app.feature.home.data.HomePregnancyOperationalStatus

class HomeRemoteMapper(
    private val dateTimeProvider: HomeDateTimeProvider
) {

    fun map(response: HomeResponseDto): HomeDashboardData = HomeDashboardData(
        userName = response.usuario.nome,
        dateTime = dateTimeProvider.now(),
        attentionItems = response.alertas.map(::mapAlert),
        upcomingPregnancies = response.proximasGestantes.map(::mapPregnancy),
        todayAppointments = response.agendaHoje.map(::mapAppointment)
    )

    private fun mapAlert(alert: HomeAlertaDto): HomeAttentionData {
        val type = HomePendingType.fromApi(alert.tipo)
        val quantity = alert.quantidade.takeIf { it > 0 } ?: throw HomePayloadException()
        val level = when (alert.nivel) {
            "INFO" -> HomeAttentionLevel.INFO
            "ATTENTION" -> HomeAttentionLevel.ATTENTION
            "ERROR" -> HomeAttentionLevel.ERROR
            else -> throw HomePayloadException()
        }
        return HomeAttentionData(
            id = type.apiValue.lowercase(),
            summary = type.summary(quantity),
            quantity = quantity,
            level = level,
            type = type,
            relatedPatientIds = alert.pacienteIds.map(Long::toString)
        )
    }

    private fun mapPregnancy(pregnancy: HomeProximaGestanteDto): HomePregnancyData {
        val pendingTypes = pregnancy.situacaoOperacional.pendencias.map(HomePendingType::fromApi)
        val actionRequired = when (pregnancy.situacaoOperacional.status) {
            "EM_DIA" -> {
                if (pendingTypes.isNotEmpty()) throw HomePayloadException()
                null
            }
            "ACAO_PENDENTE" -> pendingTypes
                .takeIf { it.isNotEmpty() }
                ?.joinToString(separator = ", ") { it.actionLabel }
                ?: "Ação pendente"
            else -> throw HomePayloadException()
        }
        val operationalStatus = when (pregnancy.situacaoOperacional.status) {
            "EM_DIA" -> HomePregnancyOperationalStatus.ON_TRACK
            "ACAO_PENDENTE" -> HomePregnancyOperationalStatus.ACTION_REQUIRED
            else -> throw HomePayloadException()
        }
        return HomePregnancyData(
            id = pregnancy.pacienteId.toString(),
            name = pregnancy.nomeGestante,
            babyName = pregnancy.nomeBebe,
            gestationalWeeks = pregnancy.idadeGestacional.semanas,
            gestationalDays = pregnancy.idadeGestacional.dias,
            dueDate = formatDueDate(pregnancy.dataProvavelParto),
            responsible = pregnancy.responsavelPrincipal?.nome,
            daysUntilDueDate = pregnancy.diasAteDpp,
            actionRequired = actionRequired,
            operationalStatus = operationalStatus,
            pendingTypes = pendingTypes
        )
    }

    private fun mapAppointment(appointment: HomeAgendaEventoDto): HomeAppointmentData {
        val (hour, minute) = parseTime(appointment.inicioEm)
        val (endHour, endMinute) = parseTime(appointment.fimEm)
        return HomeAppointmentData(
            id = appointment.id.toString(),
            time = "${hour.twoDigits()}:${minute.twoDigits()}",
            minutesFromMidnight = hour * 60 + minute,
            type = appointmentTypeLabel(appointment.tipo, appointment.titulo),
            relatedPerson = appointment.nomeGestante ?: appointment.titulo,
            endTime = "${endHour.twoDigits()}:${endMinute.twoDigits()}",
            patientId = appointment.pacienteId?.toString()
        )
    }

    private fun formatDueDate(value: String): String {
        val parts = value.split('-')
        if (parts.size != 3) throw HomePayloadException()
        val month = parts[1].toIntOrNull() ?: throw HomePayloadException()
        val day = parts[2].toIntOrNull() ?: throw HomePayloadException()
        val monthLabel = MONTHS.getOrNull(month - 1) ?: throw HomePayloadException()
        if (day !in 1..31) throw HomePayloadException()
        return "${day.twoDigits()} $monthLabel"
    }

    private fun parseTime(value: String): Pair<Int, Int> {
        val time = value.substringAfter('T', missingDelimiterValue = "")
        val parts = time.split(':')
        if (parts.size < 2) throw HomePayloadException()
        val hour = parts[0].toIntOrNull() ?: throw HomePayloadException()
        val minute = parts[1].toIntOrNull() ?: throw HomePayloadException()
        if (hour !in 0..23 || minute !in 0..59) throw HomePayloadException()
        return hour to minute
    }

    private fun appointmentTypeLabel(type: String, title: String): String = when (type) {
        "REUNIAO_APRESENTACAO" -> "Reunião"
        "OFICINA_PREPARACAO",
        "OFICINA_AMAMENTACAO",
        "OFICINA_CUIDADOS_RN" -> "Oficina"
        "OUTRO" -> title
        else -> title.ifBlank { "Compromisso" }
    }

    private fun Int.twoDigits(): String = toString().padStart(2, '0')

    private companion object {
        val MONTHS = listOf(
            "jan", "fev", "mar", "abr", "mai", "jun",
            "jul", "ago", "set", "out", "nov", "dez"
        )
    }
}

private fun HomePendingType.summary(quantity: Int): String = when (this) {
    HomePendingType.WORKSHOP -> if (quantity == 1) "Oficina pendente" else "$quantity oficinas pendentes"
    HomePendingType.FOLLOW_UP -> if (quantity == 1) "Follow-up pendente" else "$quantity follow-ups pendentes"
    HomePendingType.SIGNATURE -> if (quantity == 1) "Assinatura pendente" else "$quantity assinaturas pendentes"
}

private val HomePendingType.actionLabel: String
    get() = when (this) {
        HomePendingType.WORKSHOP -> "Oficina de preparação pendente"
        HomePendingType.FOLLOW_UP -> "Follow-up pendente"
        HomePendingType.SIGNATURE -> "Assinatura pendente"
    }

private val HomePendingType.apiValue: String
    get() = when (this) {
        HomePendingType.WORKSHOP -> "OFICINA_PENDENTE"
        HomePendingType.FOLLOW_UP -> "FOLLOW_UP_PENDENTE"
        HomePendingType.SIGNATURE -> "ASSINATURA_PENDENTE"
    }

private fun HomePendingType.Companion.fromApi(value: String): HomePendingType = when (value) {
    "OFICINA_PENDENTE" -> HomePendingType.WORKSHOP
    "FOLLOW_UP_PENDENTE" -> HomePendingType.FOLLOW_UP
    "ASSINATURA_PENDENTE" -> HomePendingType.SIGNATURE
    else -> throw HomePayloadException()
}
